"use client";

import React, { useState, useEffect } from "react";
import Link from "next/link";
import { ArrowLeft, BadgeCheck } from "lucide-react";

export function AboutClient() {
  const siteName = process.env.NEXT_PUBLIC_SITE_NAME ?? "Meazora";
  const siteUrl = process.env.NEXT_PUBLIC_SITE_URL ?? "https://www.meazora.com";
  const domain = siteUrl.replace("https://", "").replace("http://", "");
  const [activeSection, setActiveSection] = useState("origin");

  useEffect(() => {
    const sections = document.querySelectorAll("section[id]");
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { rootMargin: "-20% 0px -60% 0px" }
    );

    sections.forEach((section) => observer.observe(section));
    return () => {
      sections.forEach((section) => observer.unobserve(section));
    };
  }, []);

  const menuItems = [
    { id: "origin", label: "1. The Origin Story" },
    { id: "creator", label: "2. Creator & Lead Editor" },
    { id: "editorial-team", label: "3. Project Standards" },
    { id: "privacy-pillar", label: "4. Privacy by Design" },
    { id: "math-accuracy", label: "5. Auditing & Accuracy" },
    { id: "independence", label: "6. Platform Independence" },
    { id: "feedback", label: "7. Feedback & Support" },
  ];

  return (
    <div className="max-w-6xl mx-auto py-10 px-4 md:px-6">
      {/* Back Link */}
      <div className="mb-10 animate-in fade-in slide-in-from-top-4 duration-300">
        <Link
          href="/"
          className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-500 hover:text-slate-850 dark:text-zinc-400 dark:hover:text-zinc-200 transition-colors group font-sans"
        >
          <ArrowLeft size={14} className="group-hover:-translate-x-0.5 transition-transform" />
          Back to Calculators
        </Link>
      </div>

      {/* Header */}
      <div className="border-b border-slate-200 dark:border-zinc-800 pb-8 mb-12 animate-in fade-in duration-500">
        <span className="text-[10px] font-mono font-bold uppercase tracking-widest text-sky-600 dark:text-sky-400">
          The Philosophy
        </span>
        <h1 className="text-3xl md:text-5xl font-black text-slate-900 dark:text-slate-50 mt-2 mb-4 tracking-tight leading-none font-sans">
          About {siteName}
        </h1>
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-slate-500 dark:text-zinc-500 font-mono">
          <span>Project Mission</span>
          <span className="w-1.5 h-1.5 rounded-full bg-slate-300 dark:bg-zinc-750" />
          <span>Independent & Self-Funded</span>
        </div>
      </div>

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-12 items-start">
        {/* Sticky Sidebar */}
        <aside className="lg:col-span-1 sticky top-24 hidden lg:block select-none">
          <nav className="flex flex-col gap-3 font-sans text-xs">
            <span className="text-[9px] font-bold text-slate-400 dark:text-zinc-500 uppercase tracking-widest pl-3 mb-1">
              Mission Sections
            </span>
            {menuItems.map((item) => (
              <a
                key={item.id}
                href={`#${item.id}`}
                className={`py-1.5 pl-3 border-l-2 text-left font-medium transition-all duration-200 ${
                  activeSection === item.id
                    ? "border-sky-600 dark:border-sky-450 text-sky-600 dark:text-sky-400 font-semibold"
                    : "border-transparent text-slate-400 dark:text-zinc-500 hover:text-slate-700 dark:hover:text-zinc-300"
                }`}
              >
                {item.label}
              </a>
            ))}
          </nav>
        </aside>

        {/* Content Area */}
        <div className="lg:col-span-3 space-y-16 font-sans text-slate-650 dark:text-zinc-300">
          
          {/* Section 1: The Origin Story */}
          <section id="origin" className="scroll-mt-28 space-y-4">
            <h2 className="text-xl font-bold text-slate-900 dark:text-slate-50 tracking-tight font-sans">
              1. The Origin Story
            </h2>
            <p className="text-sm leading-relaxed">
              {siteName} was born out of frustration with existing home improvement and DIY project calculators online. Most modern material estimation web pages are either oversimplified clickbait tools designed to gather your phone number for lead generation, or bloated sites full of intrusive ads that crash on mobile devices.
            </p>
            <p className="text-sm leading-relaxed">
              We wanted a platform where we could calculate actual structural slab concrete yards, paint wall sheens, subfloor deflection limit codes, fence post placements, and drywall sheet cuts—without giving away our personal contact info or waiting for sales pitches.
            </p>
            <p className="text-sm leading-relaxed">
              This site is a completely independent effort. There are no venture capital expectations, no contractor affiliate programs pushing unnecessary upgrades, and no backend servers storing your home dimensions. It is a tool designed to calculate numbers simply, cleanly, and privately.
            </p>
            <div className="border-l-2 border-sky-500/50 pl-4 py-1.5 my-4 bg-slate-50/50 dark:bg-zinc-900/30 rounded-r-lg text-xs text-slate-550 dark:text-zinc-400 leading-relaxed italic">
              {"\"Most online estimators aren't calculators—they are lead generation funnels for sub-contracting networks. We wanted to build a pure calculator for builders and DIYers.\""}
            </div>
          </section>

          {/* Section 2: Meet the Creator & Lead Editor */}
          <section id="creator" className="scroll-mt-28 space-y-4">
            <h2 className="text-xl font-bold text-slate-900 dark:text-slate-50 tracking-tight font-sans">
              2. Meet the Creator &amp; Lead Editor
            </h2>
            <p className="text-sm leading-relaxed">
              {siteName} is developed and maintained by <strong>veliona</strong>, an independent software developer and builder, in collaboration with <strong><Link href="/authors/mauricio-l/" className="text-sky-600 dark:text-sky-400 font-semibold hover:underline">Mauricio L.</Link></strong>, our Lead Construction Editor.
            </p>
            <p className="text-sm leading-relaxed">
              With a background in building performance-focused web architectures and a personal commitment to user privacy, veliona created this platform to bridge the gap between technical construction equations and simple, client-side web utility.
            </p>
            <p className="text-sm leading-relaxed">
              Mauricio L. brings over 15 years of general contracting, carpentry, and building inspection experience to the project, managing our dataset accuracy, cost ranges, and code compliance audits. Together, we ensure every calculator runs 100% locally in your browser, maintaining absolute privacy while delivering trade-verified precision.
            </p>
          </section>

          {/* Section 3: Editorial Standards */}
          <section id="editorial-team" className="scroll-mt-28 space-y-6">
            <h2 className="text-xl font-bold text-slate-900 dark:text-slate-50 tracking-tight font-sans">
              3. Project & Data Standards
            </h2>
            <p className="text-sm leading-relaxed">
              Every formula, material constant, waste factor, and standard index on {siteName} is thoroughly researched and audited before publication. Our content follows strict accuracy guidelines, cross-referencing all technical specifications against primary construction authority publications, including the International Code Council (ICC), ASTM standards, and structural guidelines.
            </p>

            <p className="text-sm leading-relaxed">
              We check our formulas against actual building codes and trade standards. For example, concrete estimates are based on American Concrete Institute (ACI) guidelines, and framing or drywall spacing follows standard residential building practices. Before publishing a calculator, we test the geometry ourselves to make sure the math doesn&apos;t leave you short on materials when you actually place an order.
            </p>
          </section>

          {/* Section 4: Privacy by Design */}
          <section id="privacy-pillar" className="scroll-mt-28 space-y-4">
            <h2 className="text-xl font-bold text-slate-900 dark:text-slate-50 tracking-tight font-sans">
              4. Privacy by Design
            </h2>
            <p className="text-sm leading-relaxed">
              Our fundamental architecture is built around absolute client-side execution. This means when you input dimensions inside our calculators—such as room areas, wall lengths, or concrete depths—the mathematical equations execute completely within your web browser sandbox.
            </p>
            <p className="text-sm leading-relaxed">
              Your planning inputs are never transmitted to a cloud server, saved to external databases, or logged for analytics. Because we do not store your specifications, it is physically impossible for us to sell your project details to contractors, share them with advertisers, or leak them.
            </p>
            <p className="text-sm leading-relaxed">
              {"To save your calculations between tabs or visits, we use browser "}
              <code className="px-1.5 py-0.5 rounded bg-slate-100 dark:bg-zinc-800 text-[10px] font-mono text-slate-800 dark:text-zinc-200">localStorage</code>
              {". This data stays strictly on your machine and can be cleared instantly using your browser's history settings."}
            </p>
          </section>

          {/* Section 5: Auditing & Accuracy */}
          <section id="math-accuracy" className="scroll-mt-28 space-y-4">
            <h2 className="text-xl font-bold text-slate-900 dark:text-slate-50 tracking-tight font-sans">
              5. Auditing &amp; Accuracy
            </h2>
            <p className="text-sm leading-relaxed">
              Building guidelines, ASTM material grades, structural safety parameters, and manufacturer tolerances adjust over time. To maintain high accuracy, we audit our formulas and specs regularly.
            </p>
            <p className="text-sm leading-relaxed">
              Rather than using crude rough approximations, our calculators reference parameters set by recognized trade authorities:
            </p>
            
            {/* Source Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
              <div className="p-4 rounded-xl border border-slate-200 dark:border-zinc-800 bg-slate-50/30 dark:bg-zinc-900/10 space-y-2">
                <span className="font-bold text-xs text-slate-900 dark:text-slate-200 font-mono tracking-wider block">
                  ACI &amp; NRMCA STANDARDS
                </span>
                <p className="text-xs text-slate-550 dark:text-zinc-400 leading-relaxed">
                  Compressive strengths (PSI), subbase compaction layers, slab curing times, and moisture guidelines for structural pours.
                </p>
              </div>
              <div className="p-4 rounded-xl border border-slate-200 dark:border-zinc-800 bg-slate-50/30 dark:bg-zinc-900/10 space-y-2">
                <span className="font-bold text-xs text-slate-900 dark:text-slate-200 font-mono tracking-wider block">
                  TCNA &amp; ANSI CODE
                </span>
                <p className="text-xs text-slate-550 dark:text-zinc-400 leading-relaxed">
                  Floor deflection tolerances (L/360), grout joint widths, slip resistance factors (DCOF), and underlayment guidelines.
                </p>
              </div>
              <div className="p-4 rounded-xl border border-slate-200 dark:border-zinc-800 bg-slate-50/30 dark:bg-zinc-900/10 space-y-2">
                <span className="font-bold text-xs text-slate-900 dark:text-slate-200 font-mono tracking-wider block">
                  GYPSUM ASSOCIATION (GA)
                </span>
                <p className="text-xs text-slate-550 dark:text-zinc-400 leading-relaxed">
                  Fastener spacing rules, board thicknesses (standard vs Type X), and drywall finishing specification levels.
                </p>
              </div>
              <div className="p-4 rounded-xl border border-slate-200 dark:border-zinc-800 bg-slate-50/30 dark:bg-zinc-900/10 space-y-2">
                <span className="font-bold text-xs text-slate-900 dark:text-slate-200 font-mono tracking-wider block">
                  IRC CODES &amp; ASTM
                </span>
                <p className="text-xs text-slate-550 dark:text-zinc-400 leading-relaxed">
                  Residential joist spans, ceiling heights, fence post depths for wind loads, and ground-contact wood treat classifications.
                </p>
              </div>
            </div>
          </section>

          {/* Section 6: Platform Independence */}
          <section id="independence" className="scroll-mt-28 space-y-4">
            <h2 className="text-xl font-bold text-slate-900 dark:text-slate-50 tracking-tight font-sans">
              6. Platform Independence
            </h2>
            <p className="text-sm leading-relaxed">
              We do not accept sponsored affiliate placements to push specific contractor networks or materials suppliers. {siteName} is funded strictly through non-intrusive display advertising.
            </p>
            <p className="text-sm leading-relaxed">
              This choice keeps our recommendations and calculation results mathematically objective. Our goal is to present exact estimates based on geometric reality, not to nudge you toward hiring sub-contractors or purchasing extra material.
            </p>
          </section>

          {/* Section 7: Feedback & Support */}
          <section id="feedback" className="scroll-mt-28 space-y-6">
            <h2 className="text-xl font-bold text-slate-900 dark:text-slate-50 tracking-tight font-sans">
              7. Feedback &amp; Support
            </h2>
            <p className="text-sm leading-relaxed">
              We review calculator calculations and formulas regularly. If you find a mathematical discrepancy, notice a changing building code standard, or have suggestions for new home improvement tools, we want to hear about it.
            </p>
            <p className="text-sm leading-relaxed">
              You can contact us directly at <a href={`mailto:support@${domain}`} className="text-sky-600 dark:text-sky-400 hover:underline font-mono">support@{domain}</a> or use our dedicated <Link href="/contact/" className="text-sky-600 dark:text-sky-400 hover:underline font-semibold">Contact Form</Link>.
            </p>

            {/* Disclaimer Block */}
            <div className="w-full border-t border-slate-200 dark:border-zinc-800/70 py-6 mt-8 text-left">
              <span className="text-[9px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest font-mono block mb-2">
                Educational Disclaimer
              </span>
              <p className="text-[11px] text-slate-500 dark:text-slate-450 leading-relaxed font-sans">
                All material estimates and project calculations generated by {siteName} are models for planning purposes only. They do not represent certified professional architectural plans, stamped engineering specifications, or binding contractor bids. Always consult a licensed local contractor or professional engineer before beginning structural home improvements.{" "}
                <Link
                  href="/disclaimer/"
                  className="text-slate-600 dark:text-slate-300 hover:text-sky-600 dark:hover:text-sky-400 underline underline-offset-2 font-semibold transition-colors"
                >
                  Read full disclaimer &rarr;
                </Link>
              </p>
            </div>
          </section>

        </div>
      </div>
    </div>
  );
}
